import { useEffect, useRef, useState } from "react";
import logo from "../assets/logo.png";

const navLinks = [
  { label: "Features", href: "#features" },
  { label: "Company", href: "#company", hasChevron: true },
  { label: "Privacy", external: true },
];

const Header = () => {
  const isProd = process.env.REACT_APP_ENV === "production";
  const privacyPolicy = isProd ? process.env.REACT_APP_PRIVACY_PROD : process.env.REACT_APP_PRIVACY_DEV || "https://legal.luupli.com/PrivacyPolicy/";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const companyRef = useRef(null);
  const closeTimerRef = useRef(null);

  const openCompanyMenu = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setCompanyOpen(true);
  };

  const closeCompanyMenuWithDelay = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setCompanyOpen(false);
      closeTimerRef.current = null;
    }, 180);
  };

  useEffect(() => {
    const onDocClick = (event) => {
      if (companyRef.current && !companyRef.current.contains(event.target)) {
        setCompanyOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const scrollTo = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    setCompanyOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className="relative z-40 bg-white pt-5 md:pt-7">
      <div className="max-w-[1520px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="h-[64px] md:h-[76px] rounded-2xl bg-white shadow-[0_4px_30px_rgba(0,0,0,0.08)] px-5 md:px-8 flex items-center justify-between">
          <a href="#top" onClick={(e) => scrollTo(e, "#top")} className="shrink-0">
            <img src={logo} alt="luupli" className="h-8 md:h-9 w-auto" />
          </a>

          <div className="hidden md:flex items-center ml-auto gap-6 lg:gap-8">
            <nav className="flex items-center gap-6 lg:gap-8">
              {navLinks.map((link) => {
                if (link.hasChevron) {
                  return (
                    <div
                      key={link.label}
                      ref={companyRef}
                      className="relative"
                      onMouseEnter={openCompanyMenu}
                      onMouseLeave={closeCompanyMenuWithDelay}
                    >
                      <button
                        onClick={() => setCompanyOpen((prev) => !prev)}
                        className="flex items-center gap-1 text-[17px] leading-none font-medium text-[#3f3f46] hover:text-[#2fae7b] transition-colors bg-transparent cursor-pointer"
                      >
                        {link.label}
                        <svg
                          className="w-[14px] h-[14px] mt-[1px]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d={companyOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
                          />
                        </svg>
                      </button>

                      {companyOpen && (
                        <div
                          className="absolute top-[calc(100%+14px)] right-[-118px] w-[250px] bg-[#f6f6f6] border border-[#eeeeee] rounded-2xl py-3 shadow-[0_20px_30px_rgba(0,0,0,0.1)]"
                          onMouseEnter={openCompanyMenu}
                          onMouseLeave={closeCompanyMenuWithDelay}
                        >
                          <a
                            href="#as-seen-on"
                            onClick={(e) => scrollTo(e, "#as-seen-on")}
                            className="block px-5 py-3 text-[17px] leading-none text-[#4b4b4b] hover:bg-[#efefef] no-underline"
                          >
                            As Seen On
                          </a>
                          <a
                            href="#partners"
                            onClick={(e) => scrollTo(e, "#partners")}
                            className="block px-5 py-3 text-[17px] leading-none text-[#4b4b4b] hover:bg-[#efefef] no-underline"
                          >
                            Backed by Partners
                          </a>
                          <a
                            href="#team"
                            onClick={(e) => scrollTo(e, "#team")}
                            className="block px-5 py-3 text-[17px] leading-none text-[#4b4b4b] hover:bg-[#efefef] no-underline"
                          >
                            Team
                          </a>
                        </div>
                      )}
                    </div>
                  );
                }

                if (link.external && link.label === "Privacy") {
                  return (
                    <a
                      key={link.label}
                      href={privacyPolicy}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[17px] leading-none font-medium text-[#3f3f46] hover:text-[#2fae7b] hover:underline hover:underline-offset-[6px] transition-colors no-underline"
                    >
                      {link.label}
                    </a>
                  );
                }

                return (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => scrollTo(e, link.href)}
                    className="text-[17px] leading-none font-medium text-[#3f3f46] hover:text-[#2fae7b] hover:underline hover:underline-offset-[6px] transition-colors no-underline"
                  >
                    {link.label}
                  </a>
                );
              })}
            </nav>

            <a
              href="#download"
              onClick={(e) => scrollTo(e, "#download")}
              className="inline-flex items-center gap-2 px-5 h-[42px] rounded-full bg-[#9af09c] text-[#171717] text-[19px] leading-none font-medium hover:bg-[#000000] hover:text-white transition-colors no-underline"
            >
              Get the App
              <svg
                className="w-4 h-4 mt-[2px]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 bg-transparent cursor-pointer"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <svg
                className="w-6 h-6 text-gray-900"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 text-gray-900"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-[#f4f4f4] px-4">
          <div className="max-w-[1520px] mx-auto mt-2 rounded-2xl bg-white border border-gray-200 px-5 pb-5 pt-4">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) =>
                link.external && link.label === "Privacy" ? (
                  <a
                    key={link.label}
                    href={privacyPolicy}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileOpen(false)}
                    className="text-base font-medium text-gray-700 no-underline"
                  >
                    {link.label}
                  </a>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => scrollTo(e, link.href)}
                    className="text-base font-medium text-gray-700 no-underline"
                  >
                    {link.label}
                  </a>
                )
              )}
              <a
                href="#as-seen-on"
                onClick={(e) => scrollTo(e, "#as-seen-on")}
                className="pl-3 text-sm text-gray-500 no-underline"
              >
                As Seen On
              </a>
              <a
                href="#partners"
                onClick={(e) => scrollTo(e, "#partners")}
                className="pl-3 text-sm text-gray-500 no-underline"
              >
                Backed by Partners
              </a>
              <a
                href="#team"
                onClick={(e) => scrollTo(e, "#team")}
                className="pl-3 text-sm text-gray-500 no-underline"
              >
                Team
              </a>
            </nav>
            <a
              href="#download"
              onClick={(e) => scrollTo(e, "#download")}
              className="mt-5 flex items-center justify-center gap-2 w-full h-[42px] rounded-full bg-[#9af09c] text-[#163218] text-base font-medium no-underline"
            >
              Get the App
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
